<script lang="ts">
	import { beforeUpdate } from 'svelte';
	// Stores
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/components/Chart/chart_config';

	// Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_control_component_channel_structure,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	// Design System Components
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';

	// Components
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import ThumbDeviceChartV2 from 'src/components/Chart/ThumbDeviceChartV2.svelte';
	import PeakPowerInfoV2 from 'src/components/Consumption/PeakPowerInfoV2.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	// Types
	import type { Device } from 'src/types/Device.ts';

	export let device: Device;
	let id = device?.id;
	let deviceState: any = getDeviceState(id);

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	// Device calculations
	$: isGen2 = is_gen_2(device);
	$: mode = get_mode(device, status, settings);
	$: deviceConfig = devices_config?.[device?.type];

	$: _statuses = status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: labelList = _statuses.filter((e) => {
		return 'undefined' === typeof e.main && e.name !== 'state';
	});

	let timerLabels: Record<string, any> = {};
	let timer = false;

	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	beforeUpdate(async () => {
		timer =
			is_gen_2(device) &&
			typeof status?.[`${get_gen_2_component_channel_structure(device)}`]?.timer_started_at !==
				'undefined';
	});

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	const handleOnClickThumbChart = (e) => {
		console.log('Chart clicked', e);
	};
</script>

<DeviceViewStateWrapper {device}>
	<div slot="staticTop">
		{#if timer}
			{#key status[get_gen_2_control_component_channel_structure(device)].timer_started_at}
				<TimerRemaining timerKey="gen2_main" {device} {status} {settings} bind:timerLabels />
			{/key}
		{/if}

		<DeviceLabelList labelList={combinedLabelList} />
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div class="flex-full-height justify-content-end" slot="content">
		{#if deviceChartsConfig.length}
			<ThumbDeviceChartV2 {device} {deviceChartsConfig} on:click={handleOnClickThumbChart} />
		{/if}

		{#if devices_config[device.type]?.isPM}
			<PeakPowerInfoV2 {device} />
			<ParameterCardV2
				size="s"
				class="mb-s"
				icon={mapIconToThin('fas fa-plug')}
				title={_statuses.filter((s) => s.name === 'power')[0]?.value}
				description={capFirstChar($format('collapse_title_power', { default: 'Power' }))}
				backgroundColor="--color-0"
			/>
		{/if}

		<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
	</div>
</DeviceViewStateWrapper>
